import React from 'react';

import { LoadableElement } from '@99designs/common/components/LoadableElement';
import { getPreziBrief, getPreziBriefVariables } from '__generated__/getPreziBrief';
import { PreziBriefFields } from '__generated__/PreziBriefFields';
import { updatePreziBrief, updatePreziBriefVariables } from '__generated__/updatePreziBrief';
import { handleGraphQLError } from 'common/utils/Apollo';
import { AuthConsumer } from 'projects/components/AuthContext';
import DesignerMetaContext, { DesignerMetaLayouts } from 'projects/components/DesignerMetaContext';
import { errorNotFound, errorServerError } from 'projects/components/ErrorBoundary';
import { routeForBrief } from 'projects/routes';
import Brief from 'projects/routes/Brief';
import InviteDesignersPage from 'projects/routes/InviteDesignersPage';
import { Industries } from 'projects/util/industries';
import { createStepState, StepConfiguration } from 'projects/util/step';
import { ApolloConsumer, Query } from 'react-apollo';
import { Redirect, RouteComponentProps, withRouter } from 'react-router';
import { Route, Switch } from 'react-router-dom';
import AboutYouPage from './AboutYouPage';
import DetailsPage from './DetailsPage';
import queries from './PreziBrief.graphql';
import SummaryPage from './SummaryPage';
import TimeAndBudgetPage from './TimeAndBudgetPage';

type Props = RouteComponentProps<{
    briefId: string;
    step: string;
}>;

export const PreziBrief: React.FunctionComponent<Props> = props => (
    <Query<getPreziBrief, getPreziBriefVariables>
        query={queries.getPreziBrief}
        variables={{ id: props.match.params.briefId }}
    >
        {({ error, loading, data }) => {
            if (loading) {
                return <LoadableElement variations={['fade-in']} isLoading={true} />;
            }

            if (error) {
                handleGraphQLError(error);
            }

            if (!data) {
                throw errorServerError;
            }

            if (!data.brief) {
                throw errorNotFound;
            }

            if (data.brief.fields.__typename !== 'PreziBriefFields') {
                return (
                    <Redirect
                        to={routeForBrief(
                            data.brief.fields.__typename,
                            data.brief.id,
                            props.location.search
                        )}
                    />
                );
            }

            const steps: StepConfiguration[] = [
                {
                    step: 1,
                    title: __('Tell us about your Prezi project'),
                },
                { step: 2, title: __('Your timeline') },
                { step: 3, title: __('About you') },
                { step: 4, title: __('Review your brief') },
                {
                    step: 5,
                    title: __('Invite designers to quote'),
                    requiresAuth: true,
                    isDesignerStep: true,
                    nextActionLabel: __('Confirm designers'),
                },
            ];

            const step = createStepState({
                steps,
                brief: data.brief,
                currentStep: parseInt(props.match.params.step, 10),
                designerSources: data.brief.designersWithSource.map(d => d.source),
            });

            const brief = {
                ...data.brief,
                fields: data.brief.fields as PreziBriefFields,
            };

            const industries = new Industries(data.industries);

            return (
                <ApolloConsumer>
                    {client => (
                        <AuthConsumer>
                            {({ login }) => (
                                <Brief
                                    brief={brief}
                                    viewer={data.viewer}
                                    login={login}
                                    step={step}
                                    client={client}
                                    onSave={async changes => {
                                        const res = await client.mutate<
                                            updatePreziBrief,
                                            updatePreziBriefVariables
                                        >({
                                            mutation: queries.updatePreziBrief,
                                            variables: {
                                                id: brief.id,
                                                fields: { prezi: changes },
                                            },
                                        });
                                        return res ? res.data!.brief.errors : [];
                                    }}
                                >
                                    {form => (
                                        <Switch>
                                            <Route
                                                path={`/briefs/prezi/:briefId/1`}
                                                render={() => (
                                                    <DetailsPage form={form} brief={brief} />
                                                )}
                                            />
                                            <Route
                                                path={`/briefs/prezi/:briefId/2`}
                                                render={() => (
                                                    <TimeAndBudgetPage
                                                        form={form}
                                                        brief={brief}
                                                        site={data.site}
                                                    />
                                                )}
                                            />
                                            <Route
                                                path={`/briefs/prezi/:briefId/3`}
                                                render={() => (
                                                    <AboutYouPage
                                                        form={form}
                                                        brief={brief}
                                                        industries={data.industries}
                                                    />
                                                )}
                                            />
                                            <Route
                                                path={`/briefs/prezi/:briefId/4`}
                                                render={() => (
                                                    <SummaryPage
                                                        brief={brief}
                                                        industries={industries}
                                                    />
                                                )}
                                            />
                                            <Route
                                                path={`/briefs/prezi/:briefId/5`}
                                                render={() => (
                                                    <DesignerMetaContext.Provider
                                                        value={{
                                                            layout: DesignerMetaLayouts.compact,
                                                            hasMoreResultsUrl: false,
                                                            hasResponsiveness: false,
                                                        }}
                                                    >
                                                        <InviteDesignersPage
                                                            brief={brief}
                                                            viewer={data.viewer}
                                                        >
                                                            {__(
                                                                'Here are the Prezi Certified Experts that best match your needs'
                                                            )}
                                                        </InviteDesignersPage>
                                                    </DesignerMetaContext.Provider>
                                                )}
                                            />
                                        </Switch>
                                    )}
                                </Brief>
                            )}
                        </AuthConsumer>
                    )}
                </ApolloConsumer>
            );
        }}
    </Query>
);

export default withRouter(PreziBrief);
